import React, { useState } from 'react';
import {
    DropdownSelect,
    components,
    type OptionProps,
    type SingleValueProps,
    type GroupBase,
} from 'src/components/DropdownWrapper/DropdownWrapper';

import { observer } from 'src/utils/mobx-react';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { CountryDropdownNewState, SelectOptionType } from 'src/components/CountryDropdown/CountryDropdownNew.state';
import { InputError, InputLabel } from 'src/domains/layouts/shared/formComponents/FormComponents.style';
import {
    CountryItem,
    CountryItemWrapper,
    OptionWrapper,
    countryDropdownStyles,
} from 'src/components/CountryDropdown/CountryDropdownNew.style';

interface CountryDropdownNewPropsType {
    stateValue: FormInputState<string, string>;
    options: SelectOptionType[];
    label: string;
    prefix?: string;
}

const parseCountryKey = (key: string): { countryCode: string; countryName: string } => {
    const lastDashIndex = key.lastIndexOf('-');
    return {
        countryCode: key.slice(lastDashIndex + 1).toLowerCase(),
        countryName: key.slice(0, lastDashIndex),
    };
};

const CountryFlag = ({ countryKey }: { countryKey: string }): JSX.Element => {
    const { countryCode, countryName } = parseCountryKey(countryKey);

    return (
        <CountryItemWrapper>
            <CountryItem
                className={`famfamfam-flags ${countryCode}`}
                data-test='country-dropdown-new-flag'
            />
            {countryName}
        </CountryItemWrapper>
    );
};

const CustomOption = (props: OptionProps<SelectOptionType, false, GroupBase<SelectOptionType>>): JSX.Element => {
    const { data, innerRef, innerProps, isSelected } = props;

    return (
        <OptionWrapper
            ref={innerRef}
            {...innerProps}
            isSelected={isSelected}
            role='option'
            aria-selected={isSelected}
        >
            <CountryFlag countryKey={data.key} />
        </OptionWrapper>
    );
};

const CustomSingleValue = (
    props: SingleValueProps<SelectOptionType, false, GroupBase<SelectOptionType>>
): JSX.Element => {
    return (
        <components.SingleValue {...props}>
            <CountryFlag countryKey={props.data.key} />
        </components.SingleValue>
    );
};

export const CountryDropdownNew = observer(
    'CountryDropdownNew',
    ({ options, stateValue, label, prefix }: CountryDropdownNewPropsType): JSX.Element | null => {
        const [state] = useState(() => new CountryDropdownNewState(stateValue));

        return (
            <>
                {label === '' ? null : <InputLabel>{label}</InputLabel>}

                <DropdownSelect<SelectOptionType>
                    options={options}
                    value={state.value}
                    onChange={state.onChange}
                    placeholder={prefix}
                    customStyles={countryDropdownStyles}
                    components={{
                        Option: CustomOption,
                        SingleValue: CustomSingleValue,
                    }}
                    getOptionValue={(option: SelectOptionType) => option.key}
                    getOptionLabel={(option: SelectOptionType) => option.name}
                />

                {stateValue.result.value.type === 'error' && <InputError>{stateValue.result.value.message}</InputError>}
            </>
        );
    }
);
